import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserStore } from '@core/user/user.store';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ChatResponse, CognigyBotPayload, CognigyChatRequest, CognigyResponse } from './ai.models';

const FALLBACK_ANSWER = 'Entschuldigung, ich habe mich verschluckt. Bitte frag nochmal.';

@Injectable({ providedIn: 'root' })
export class AIAPIService {
  private readonly http = inject(HttpClient);
  private readonly userStore = inject(UserStore);

  private sessionId = crypto.randomUUID();

  public resetSession(): void {
    this.sessionId = crypto.randomUUID();
  }

  public sendQuestion(question: string): Observable<ChatResponse> {
    const userId = this.userStore.userProfile()?.username;
    if (!userId) {
      return throwError(() => new Error('User profile is not available.'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-rest-endpoint-key': environment.wattsonEndpointKey,
    });

    const payload: CognigyChatRequest = {
      userId,
      sessionId: this.sessionId,
      text: `{env: ${environment.appIdentifier}} ${question}`,
    };

    return this.http
      .post<CognigyResponse>(environment.wattsonEndpointUrl, payload, { headers })
      .pipe(
        map((response) => this.mapCognigyResponse(response)),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  private mapCognigyResponse(response: CognigyResponse): ChatResponse {
    const text = response.text?.trim();
    if (!text) {
      return { answer: FALLBACK_ANSWER };
    }

    const { preamble, payload } = this.extractBotPayload(text);
    if (!payload) {
      return { answer: text };
    }

    const answerParts = [preamble, payload.answer].filter((part) => !!part?.trim());
    return {
      answer: answerParts.join('\n\n') || FALLBACK_ANSWER,
      highlight: payload.highlightCode ?? undefined,
    };
  }

  // Cognigy returns several messages in one response, including a greeting and a payload.
  private extractBotPayload(text: string): {
    preamble: string;
    payload: CognigyBotPayload | null;
  } {
    // We need to identify the start here because this could be text (greeting) or a payload (json).
    const jsonStart = text.indexOf('{');
    if (jsonStart === -1) {
      return { preamble: text, payload: null };
    }

    const jsonEnd = text.lastIndexOf('}');
    if (jsonEnd <= jsonStart) {
      return { preamble: text, payload: null };
    }

    const preamble = text.slice(0, jsonStart).trim();
    const jsonCandidate = text.slice(jsonStart, jsonEnd + 1);

    try {
      const parsed = JSON.parse(jsonCandidate) as CognigyBotPayload;
      if (typeof parsed.answer === 'string') {
        return { preamble, payload: parsed };
      }
    } catch {
      // fall through
    }

    return { preamble: text, payload: null };
  }
}
